import { FC, useState } from 'react'
import { createPortal } from 'react-dom'
import { ReactComponent as AnnouncementIcon } from 'src/assets/svg/announcement.svg'
import { ReactComponent as InfoIcon } from 'src/assets/svg/ri-information-fill.svg'
import { ReactComponent as ArrowUpIcon } from 'src/assets/svg/ri-arrow-up-circle-fill.svg'
import { SectionTitle } from 'src/components/SectionTitle'
import { Skeleton } from 'src/components/Skeleton'
import classes from './operatorPerformanceBreakdown.module.scss'

// From operatorPerformanceBreakdown.module.scss: .statValue is 20px/100%, so
// the placeholder fills the same line box inside the fixed 88px .statCard.
const STAT_VALUE_HEIGHT = 20

const PLATFORM_OPTIONS = [
  { id: 'all', label: 'All Platforms' },
  { id: 'facebook', label: 'Facebook' },
  { id: 'instagram', label: 'Instagram' },
  { id: 'whatsapp', label: 'WhatsApp' },
  { id: 'booking', label: 'Booking' },
  { id: 'expedia', label: 'Expedia' },
] as const

interface OperatorData {
  id: string
  name: string
  sentiments: {
    negative: number
    neutral: number
    positive: number
  }
  totalSentiments: number
}

interface OperatorPerformanceBreakdownProps {
  activePlatform: string
  // Platform ids that actually have data (from the parent); tabs are filtered
  // to these so e.g. Booking/Expedia don't show when the page has none.
  availablePlatforms?: string[]
  data?: any
  isLoading?: boolean
  onDownloadReport?: () => void
  onPlatformChange: (platform: string) => void
}

interface HoveredOperator {
  operator: OperatorData
  x: number
  y: number
}

const displayOperatorName = (name?: string) =>
  !name || name === '-' ? 'Unassigned' : name

const sentimentColors = {
  negative: '#FF6833',
  neutral: '#C7CED1',
  positive: '#8080FF',
}

export const OperatorPerformanceBreakdown: FC<
  OperatorPerformanceBreakdownProps
> = ({
  activePlatform,
  availablePlatforms,
  data,
  isLoading,
  onDownloadReport,
  onPlatformChange,
}) => {
  const [hovered, setHovered] = useState<HoveredOperator | null>(null)
  // First load only. The old early return swapped the whole widget for a 400px
  // "Loading..." box under a different heading, so the section resized twice
  // on the way in; the stats also read a confident "0" operators.
  const isInitialLoading = !!isLoading && !data
  const platformOptions = PLATFORM_OPTIONS.filter(
    (platform) =>
      platform.id === 'all' || (availablePlatforms ?? []).includes(platform.id)
  )

  const operatorAssignment = data?.operatorAssignment as
    | { enabled: boolean; pageName: string | null }
    | undefined
  const operatorAssignmentDisabled = operatorAssignment?.enabled === false
  const operatorAssignmentPageName = operatorAssignment?.pageName ?? null

  // Use API data if available, otherwise use empty array
  const isApiData = data && data.operators
  const operatorData = data?.operators || []

  const mostPositiveOperator = isApiData
    ? data.stats.mostPositiveOperator
    : operatorData.length > 0
      ? operatorData.reduce((prev: OperatorData, current: OperatorData) => {
          const prevRatio =
            prev.totalSentiments > 0
              ? prev.sentiments.positive / prev.totalSentiments
              : 0
          const currentRatio =
            current.totalSentiments > 0
              ? current.sentiments.positive / current.totalSentiments
              : 0
          return currentRatio > prevRatio ? current : prev
        }, operatorData[0])
      : null

  const mostNegativeOperator = isApiData
    ? data.stats.mostNegativeOperator
    : operatorData.length > 0
      ? operatorData.reduce((prev: OperatorData, current: OperatorData) => {
          const prevRatio =
            prev.totalSentiments > 0
              ? prev.sentiments.negative / prev.totalSentiments
              : 0
          const currentRatio =
            current.totalSentiments > 0
              ? current.sentiments.negative / current.totalSentiments
              : 0
          return currentRatio > prevRatio ? current : prev
        }, operatorData[0])
      : null

  const mostNegativeSameAsPositive =
    !!mostPositiveOperator &&
    !!mostNegativeOperator &&
    (mostPositiveOperator.id === mostNegativeOperator.id ||
      mostPositiveOperator.name === mostNegativeOperator.name)

  // Calculate max value for chart scaling
  const maxSentiments = Math.max(
    ...operatorData.map((o: OperatorData) => o.totalSentiments),
    10
  )
  const chartMax = Math.ceil(maxSentiments * 1.2) // Add 20% padding

  // Generate Y-axis labels
  const yAxisLabels = []
  const step = Math.ceil(chartMax / 5)
  for (let i = 5; i >= 0; i--) {
    yAxisLabels.push(step * i)
  }

  return (
    <div className={classes.container}>
      <div className={classes.header}>
        <SectionTitle
          title="Operator Performance Breakdown"
          tooltipContent="Analyze operator performance based on sentiment distribution across customer interactions"
        />
        <div className={classes.platformTabs}>
          {platformOptions.map((platform) => (
            <button
              className={`${classes.platformTab} ${
                activePlatform === platform.id ? classes.activePlatform : ''
              }`}
              key={platform.id}
              onClick={() => onPlatformChange(platform.id)}
              type="button"
            >
              {platform.label}
            </button>
          ))}
        </div>
      </div>

      {operatorAssignmentDisabled ? (
        <div className={classes.emptyState}>
          Per-operator performance isn&apos;t available
          {operatorAssignmentPageName
            ? ` for ${operatorAssignmentPageName}`
            : ''}{' '}
          — assignment tracking isn&apos;t enabled on this page. Once
          conversations start being assigned to specific admins in Meta Business
          Suite, this view will populate automatically.
        </div>
      ) : (
        <>
          <div className={classes.infoRow}>
            <InfoIcon className={classes.infoIcon} />
            <span className={classes.infoText}>
              You are viewing top 6 operators by chat volumes - download the
              report for full list.{' '}
              <button
                className={classes.downloadLink}
                onClick={onDownloadReport}
                type="button"
              >
                Download full report
              </button>
            </span>
          </div>

          <div className={classes.statsRow}>
            <div className={classes.statCard}>
              <div className={classes.statIcon}>
                <AnnouncementIcon />
              </div>
              <div className={classes.statContent}>
                <div className={classes.statLabel}>Total Operators</div>
                <div className={classes.statValue}>
                  {isInitialLoading ? (
                    <Skeleton height={STAT_VALUE_HEIGHT} width={40} />
                  ) : (
                    operatorData.length.toLocaleString()
                  )}
                </div>
              </div>
            </div>

            <div className={classes.statCard}>
              <div className={classes.statIcon}>
                <ArrowUpIcon />
              </div>
              <div className={classes.statContent}>
                <div className={classes.statLabel}>Most Positive</div>
                <div className={classes.statValue}>
                  {isInitialLoading ? (
                    <Skeleton height={STAT_VALUE_HEIGHT} width={88} />
                  ) : (
                    mostPositiveOperator?.name || 'N/A'
                  )}
                </div>
              </div>
            </div>

            <div className={classes.statCard}>
              <div className={classes.statIcon}>
                <ArrowUpIcon style={{ transform: 'rotate(180deg)' }} />
              </div>
              <div className={classes.statContent}>
                <div className={classes.statLabel}>Most Negative</div>
                <div className={classes.statValue}>
                  {isInitialLoading ? (
                    <Skeleton height={STAT_VALUE_HEIGHT} width={88} />
                  ) : mostNegativeSameAsPositive ? (
                    '-'
                  ) : (
                    mostNegativeOperator?.name || 'N/A'
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className={classes.chartContainer}>
            <div className={classes.chart}>
              <div className={classes.yAxis}>
                {yAxisLabels.slice(0, 5).map((label) => (
                  <div className={classes.yLabel} key={label}>
                    {label > 999 ? `${(label / 1000).toFixed(1)}k` : label}
                  </div>
                ))}
              </div>

              <div className={classes.chartArea}>
                <div className={classes.gridLines}>
                  <div className={classes.gridLine} />
                  <div className={classes.gridLine} />
                  <div className={classes.gridLine} />
                  <div className={classes.gridLine} />
                  <div className={classes.gridLine} />
                </div>

                <div className={classes.bars}>
                  {operatorData.map((operator: OperatorData) => {
                    const totalSentiments = operator.totalSentiments || 0

                    // Calculate the actual bar height in pixels (max 260px)
                    const maxBarHeight = 260
                    const barHeightPixels =
                      chartMax > 0
                        ? (totalSentiments / chartMax) * maxBarHeight
                        : 0

                    // Ensure minimum height for visibility
                    const actualBarHeight =
                      totalSentiments > 0 ? Math.max(barHeightPixels, 20) : 0

                    const sortedSentiments = [
                      ['neutral', operator.sentiments.neutral],
                      ['negative', operator.sentiments.negative],
                      ['positive', operator.sentiments.positive],
                    ] as [string, number][]

                    return (
                      <div className={classes.barWrapper} key={operator.id}>
                        <div
                          className={classes.stackedBar}
                          onMouseEnter={(e) => {
                            const rect = (
                              e.currentTarget as HTMLDivElement
                            ).getBoundingClientRect()
                            setHovered({
                              operator,
                              x: rect.left + rect.width / 2,
                              y: rect.top,
                            })
                          }}
                          onMouseLeave={() => setHovered(null)}
                          style={{ height: `${actualBarHeight}px` }}
                        >
                          {sortedSentiments.map(([sentiment, value]) => {
                            // Calculate segment as percentage of total sentiments
                            const segmentPercentage =
                              totalSentiments > 0
                                ? ((value as number) / totalSentiments) * 100
                                : 0

                            return (
                              <div
                                className={classes.barSegment}
                                key={sentiment}
                                style={{
                                  backgroundColor:
                                    sentimentColors[
                                      sentiment as keyof typeof sentimentColors
                                    ],
                                  height: `${segmentPercentage}%`,
                                }}
                              />
                            )
                          })}
                        </div>
                      </div>
                    )
                  })}
                </div>

                <div className={classes.xAxis}>
                  {operatorData.map((operator: OperatorData) => (
                    <div className={classes.xLabel} key={operator.id}>
                      <span className={classes.operatorName}>
                        {displayOperatorName(operator.name)}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              <div className={classes.yAxisRight}>
                {yAxisLabels.slice(0, 5).map((label) => (
                  <div className={classes.yLabel} key={label}>
                    {label > 999 ? `${(label / 1000).toFixed(1)}k` : label}
                  </div>
                ))}
              </div>
            </div>

            {hovered &&
              createPortal(
                <div
                  className={classes.barTooltip}
                  style={{ left: hovered.x, top: hovered.y }}
                >
                  <div className={classes.tooltipTitle}>
                    {displayOperatorName(hovered.operator.name)}
                  </div>
                  <div className={classes.tooltipRow}>
                    <span className={classes.tooltipLabel}>
                      <span
                        className={classes.tooltipDot}
                        style={{ backgroundColor: sentimentColors.positive }}
                      />
                      Positive
                    </span>
                    <span className={classes.tooltipValue}>
                      {hovered.operator.sentiments.positive.toLocaleString()}
                    </span>
                  </div>
                  <div className={classes.tooltipRow}>
                    <span className={classes.tooltipLabel}>
                      <span
                        className={classes.tooltipDot}
                        style={{ backgroundColor: sentimentColors.neutral }}
                      />
                      Neutral
                    </span>
                    <span className={classes.tooltipValue}>
                      {hovered.operator.sentiments.neutral.toLocaleString()}
                    </span>
                  </div>
                  <div className={classes.tooltipRow}>
                    <span className={classes.tooltipLabel}>
                      <span
                        className={classes.tooltipDot}
                        style={{ backgroundColor: sentimentColors.negative }}
                      />
                      Negative
                    </span>
                    <span className={classes.tooltipValue}>
                      {hovered.operator.sentiments.negative.toLocaleString()}
                    </span>
                  </div>
                </div>,
                document.body
              )}

            <div className={classes.legendContainer}>
              {[
                { color: sentimentColors.positive, name: 'Positive' },
                { color: sentimentColors.neutral, name: 'Neutral' },
                { color: sentimentColors.negative, name: 'Negative' },
              ].map(({ color, name }) => (
                <div className={classes.legendItem} key={name}>
                  <div
                    className={classes.legendColor}
                    style={{ backgroundColor: color }}
                  />
                  <span className={classes.legendLabel}>{name}</span>
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  )
}
